import { useMemo, useState, useEffect } from 'react';
import { provinceCityData } from '@/mocks/provinceData';
import { calculatePF } from '@/utils/calculator';
import { fetchChinaBoundary, type ProvincePath } from '@/utils/chinaGeo';

const LON_MIN = 73;
const LON_MAX = 135;
const LAT_MIN = 18;
const LAT_MAX = 54;

interface ZoneConfig {
  key: string;
  label: string;
  min: number;
  color: string;
  bg: string;
  fill: string;
}

const ZONES: ZoneConfig[] = [
  { key: 'very-high', label: '极高持久 (PF ≥ 80)', min: 80, color: '#065f46', bg: 'rgba(6,95,70,0.14)', fill: 'rgba(6,95,70,0.5)' },
  { key: 'high', label: '高持久 (65–80)', min: 65, color: '#10b981', bg: 'rgba(16,185,129,0.14)', fill: 'rgba(16,185,129,0.5)' },
  { key: 'medium', label: '中持久 (50–65)', min: 50, color: '#f59e0b', bg: 'rgba(245,158,11,0.14)', fill: 'rgba(245,158,11,0.5)' },
  { key: 'low', label: '较低持久 (35–50)', min: 35, color: '#f97316', bg: 'rgba(249,115,22,0.14)', fill: 'rgba(249,115,22,0.5)' },
  { key: 'very-low', label: '低持久 (PF < 35)', min: -Infinity, color: '#dc2626', bg: 'rgba(220,38,38,0.14)', fill: 'rgba(220,38,38,0.5)' },
];

function getZone(pf: number): ZoneConfig {
  return ZONES.find((z) => pf >= z.min) || ZONES[ZONES.length - 1];
}

function matchProvinceKey(geoName: string): string | null {
  const keys = Object.keys(provinceCityData);
  for (const key of keys) {
    if (geoName.startsWith(key)) return key;
  }
  return null;
}

function project(lon: number, lat: number) {
  return {
    x: ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * 100,
    y: ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * 100,
  };
}

const LON_TICKS = [80, 90, 100, 110, 120, 130];
const LAT_TICKS = [20, 25, 30, 35, 40, 45, 50];

interface CityDatum {
  province: string;
  name: string;
  lat: number;
  lon: number;
  estTs: number;
}

interface ProjectedCity extends CityDatum {
  key: string;
  x: number;
  y: number;
  pf: number;
  zone: ZoneConfig;
}

interface ChinaMapProps {
  onSelectCity?: (province: string, city: string) => void;
}

export default function ChinaMap({ onSelectCity }: ChinaMapProps) {
  const [hc, setHc] = useState(0.3);
  const [hiddenZones, setHiddenZones] = useState<Set<string>>(new Set());
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [boundaryPaths, setBoundaryPaths] = useState<ProvincePath[]>([]);

  useEffect(() => {
    let active = true;
    fetchChinaBoundary()
      .then((paths) => {
        if (active) setBoundaryPaths(paths);
      })
      .catch(() => {
        // 边界加载失败时静默降级，仅显示散点
      });
    return () => {
      active = false;
    };
  }, []);

  const allCities = useMemo<CityDatum[]>(() => {
    const list: CityDatum[] = [];
    Object.entries(provinceCityData).forEach(([province, info]) => {
      info.cities.forEach((city) => {
        list.push({
          province,
          name: city.name,
          lat: city.lat,
          lon: city.lon,
          estTs: city.estTs,
        });
      });
    });
    return list;
  }, []);

  const projected = useMemo<ProjectedCity[]>(() => {
    return allCities.map((city) => {
      const { x, y } = project(city.lon, city.lat);
      const pf = calculatePF(city.estTs, hc).pf;
      const zone = getZone(pf);
      return {
        ...city,
        key: `${city.province}-${city.name}`,
        x,
        y,
        pf,
        zone,
      };
    });
  }, [allCities, hc]);

  const provincePf = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    Object.entries(provinceCityData).forEach(([province, info]) => {
      if (info.cities.length === 0) return;
      const avgTs = info.cities.reduce((sum, c) => sum + c.estTs, 0) / info.cities.length;
      map[province] = calculatePF(avgTs, hc).pf;
    });
    return map;
  }, [hc]);

  const visibleCities = projected.filter((c) => !hiddenZones.has(c.zone.key));
  const selectedCity = selectedKey ? projected.find((c) => c.key === selectedKey) : null;
  const hoveredCity = hoveredKey ? projected.find((c) => c.key === hoveredKey) : null;

  const toggleZone = (key: string) => {
    setHiddenZones((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSelectCity = (key: string) => {
    setSelectedKey((prev) => (prev === key ? null : key));
    if (onSelectCity) {
      const city = projected.find((c) => c.key === key);
      if (city) {
        onSelectCity(city.province, city.name);
      }
    }
  };

  return (
    <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70 shadow-sm">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-500/15 flex items-center justify-center">
            <i className="ri-map-pin-2-line text-primary-600 text-xl" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground-950">中国 PF 分区地图</h2>
            <p className="text-sm text-foreground-600 mt-0.5">
              基于估计年均土壤温度（0.1m 深度）绘制的持久系数空间分布参考
            </p>
          </div>
        </div>

        {/* H/C_org slider */}
        <div className="flex items-center gap-3 bg-background-100/70 rounded-xl px-4 py-3 border border-background-200/60 w-full md:w-auto">
          <span className="text-xs font-medium text-foreground-600 whitespace-nowrap">H/C_org</span>
          <input
            type="range"
            min={0.1}
            max={0.6}
            step={0.05}
            value={hc}
            onChange={(e) => setHc(parseFloat(e.target.value))}
            className="flex-1 md:flex-none md:w-44 accent-emerald-600 cursor-pointer"
            aria-label="H/C_org 值滑块"
          />
          <span className="text-sm font-semibold text-foreground-900 w-11 text-right font-mono">
            {hc.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 mb-6">
        {ZONES.map((zone) => {
          const isHidden = hiddenZones.has(zone.key);
          return (
            <button
              key={zone.key}
              onClick={() => toggleZone(zone.key)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border cursor-pointer ${
                isHidden
                  ? 'bg-background-100/50 border-background-200/50 opacity-50'
                  : 'bg-background-50 border-background-200/70'
              }`}
            >
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: zone.color }}
              />
              <span className="text-foreground-800">{zone.label}</span>
            </button>
          );
        })}
      </div>

      {/* Map area */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">
        <div
          className="relative w-full rounded-xl border border-background-200/70 bg-background-50 overflow-hidden aspect-[4/3] md:aspect-[62/36]"
        >
          {/* province boundaries */}
          {boundaryPaths.length > 0 && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 62 36"
              preserveAspectRatio="none"
            >
              {boundaryPaths.map((prov, i) => {
                const key = matchProvinceKey(prov.name);
                const pf = key ? provincePf[key] : null;
                const zone = pf != null ? getZone(pf) : null;
                return (
                  <path
                    key={i}
                    d={prov.path}
                    fill={zone ? zone.fill : 'oklch(var(--background-100))'}
                    stroke="oklch(var(--background-50))"
                    strokeWidth={0.12}
                    strokeLinejoin="round"
                  />
                );
              })}
            </svg>
          )}

          {/* graticule */}
          <div className="absolute inset-0 pointer-events-none">
            {LON_TICKS.map((lon) => {
              const x = ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * 100;
              return (
                <div
                  key={`lon-${lon}`}
                  className="absolute top-0 bottom-0 w-px bg-background-200/50"
                  style={{ left: `${x}%` }}
                />
              );
            })}
            {LAT_TICKS.map((lat) => {
              const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * 100;
              return (
                <div
                  key={`lat-${lat}`}
                  className="absolute left-0 right-0 h-px bg-background-200/50"
                  style={{ top: `${y}%` }}
                />
              );
            })}
            {/* tick labels */}
            {LON_TICKS.map((lon) => {
              const x = ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * 100;
              return (
                <span
                  key={`lon-label-${lon}`}
                  className="absolute text-[9px] text-foreground-400 font-mono -translate-x-1/2"
                  style={{ left: `${x}%`, bottom: '2px' }}
                >
                  {lon}°E
                </span>
              );
            })}
            {LAT_TICKS.map((lat) => {
              const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * 100;
              return (
                <span
                  key={`lat-label-${lat}`}
                  className="absolute text-[9px] text-foreground-400 font-mono -translate-y-1/2"
                  style={{ top: `${y}%`, left: '3px' }}
                >
                  {lat}°N
                </span>
              );
            })}
          </div>

          {/* city dots */}
          {visibleCities.map((city) => {
            const isSelected = selectedKey === city.key;
            const isHovered = hoveredKey === city.key;
            return (
              <button
                key={city.key}
                onClick={() => handleSelectCity(city.key)}
                onMouseEnter={() => setHoveredKey(city.key)}
                onMouseLeave={() => setHoveredKey(null)}
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-150 cursor-pointer"
                style={{
                  left: `${city.x}%`,
                  top: `${city.y}%`,
                  width: isSelected ? '20px' : isHovered ? '16px' : '12px',
                  height: isSelected ? '20px' : isHovered ? '16px' : '12px',
                  backgroundColor: city.zone.color,
                  zIndex: isSelected || isHovered ? 30 : 10,
                  boxShadow:
                    isSelected || isHovered
                      ? '0 0 0 3px #ffffff, 0 0 0 5px ' + city.zone.color
                      : 'none',
                }}
                aria-label={`${city.province} ${city.name}，PF ${city.pf.toFixed(1)}`}
              />
            );
          })}

          {/* hover tooltip */}
          {hoveredCity && (
            <div
              className="absolute pointer-events-none bg-white border border-background-200 rounded-lg shadow-sm px-3 py-2 text-xs z-40 -translate-x-1/2 whitespace-nowrap"
              style={{
                left: `${hoveredCity.x}%`,
                top: `calc(${hoveredCity.y}% - 40px)`,
              }}
            >
              <div className="font-semibold text-foreground-900">
                {hoveredCity.province} · {hoveredCity.name}
              </div>
              <div className="text-foreground-600 mt-0.5">
                Ts 估计 {hoveredCity.estTs.toFixed(1)}°C · PF {hoveredCity.pf.toFixed(1)}
              </div>
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="flex flex-col gap-4">
          <div className="bg-background-100/60 rounded-xl p-4 border border-background-200/60">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
              <i className="ri-information-line text-primary-600" />
              城市详情
            </h3>
            {selectedCity ? (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: selectedCity.zone.color }}
                  />
                  <span className="font-semibold text-foreground-900 text-base">
                    {selectedCity.province} · {selectedCity.name}
                  </span>
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div className="bg-background-50 rounded-lg p-2.5 border border-background-200/60">
                    <dt className="text-foreground-500 mb-0.5">估计 Ts</dt>
                    <dd className="text-foreground-900 font-mono font-semibold">
                      {selectedCity.estTs.toFixed(1)}°C
                    </dd>
                  </div>
                  <div className="bg-background-50 rounded-lg p-2.5 border border-background-200/60">
                    <dt className="text-foreground-500 mb-0.5">PF 值</dt>
                    <dd className="text-foreground-900 font-mono font-semibold">
                      {selectedCity.pf.toFixed(1)}
                    </dd>
                  </div>
                  <div className="bg-background-50 rounded-lg p-2.5 border border-background-200/60">
                    <dt className="text-foreground-500 mb-0.5">纬度</dt>
                    <dd className="text-foreground-900 font-mono">{selectedCity.lat.toFixed(2)}°N</dd>
                  </div>
                  <div className="bg-background-50 rounded-lg p-2.5 border border-background-200/60">
                    <dt className="text-foreground-500 mb-0.5">经度</dt>
                    <dd className="text-foreground-900 font-mono">{selectedCity.lon.toFixed(2)}°E</dd>
                  </div>
                </dl>
                <div
                  className="rounded-lg px-3 py-2 text-xs font-medium"
                  style={{ backgroundColor: selectedCity.zone.bg, color: selectedCity.zone.color }}
                >
                  {selectedCity.zone.label}
                </div>
              </div>
            ) : (
              <p className="text-sm text-foreground-500 leading-relaxed">
                点击地图上的城市圆点查看该地的估计土壤温度与 PF 分区详情。
              </p>
            )}
          </div>

          <div className="bg-background-100/60 rounded-xl p-4 border border-background-200/60">
            <h3 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
              <i className="ri-lightbulb-line text-accent-600" />
              分区说明
            </h3>
            <p className="text-xs text-foreground-600 leading-relaxed">
              地图使用 <strong className="text-foreground-800">估计年均土壤温度</strong>（参考气候数据）计算
              PF，用于展示空间分布趋势。颜色越<strong className="text-accent-700">暖</strong>表示持久性越低。
              精确计算请使用上方计算器（NASA POWER 实测土壤温度）。
            </p>
          </div>
        </div>
      </div>

      <p className="text-xs text-foreground-500 mt-4 text-center">
        共 {allCities.length} 个城市 · 当前显示 {visibleCities.length} 个 · 拖动 H/C_org 滑块可实时更新 PF 分区
      </p>
    </div>
  );
}